/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/pwa/versionWatch.ts
 *
 * Rôle :
 *   Détecter qu'une NOUVELLE VERSION de l'application est en ligne, même sans
 *   service worker (navigation privée, navigateur qui le refuse, console
 *   e.labriquerie.net qui n'en a pas). La version, c'est l'empreinte du
 *   fichier d'entrée (`assets/index-XXXX.js`) référencé par `index.html` :
 *   elle change à chaque build. On relit `index.html` en contournant les
 *   caches, on compare à celle qui a chargé la page ; si elle diffère, on
 *   affiche le bandeau « l'application a évolué ».
 *
 * Quand : à chaque retour sur l'onglet (visibilitychange) espacé d'au moins
 * une minute, et toutes les 5 minutes pendant que l'onglet est visible
 * (rythme du 2026-10-04 ; c'était une heure). Jamais
 * plus souvent : le serveur n'est pas interrogé à chaque bascule d'onglet.
 *
 * Ce module complète registerServiceWorker.ts, il ne le remplace pas : avec
 * un service worker en mode prompt, c'est lui qui signale ; sans, c'est ici.
 * Le bandeau est le même, avec sa garde anti-doublon.
 *
 * Mikael - chef de projet - 2026-09-20
 *
 * 2026-10-07 (Mika : « "Recharger" ne vient jamais sur mon iPhone ») : sur
 * une application installée à l'écran d'accueil d'un iPhone, RIEN ne
 * relisait index.html à l'ouverture (la première vérification attendait un
 * retour sur l'onglet après une minute, ou cinq minutes de minuteur, et iOS
 * gèle les minuteurs en arrière-plan). Désormais : une vérification au
 * démarrage (après quelques secondes), puis à CHAQUE retour au premier plan
 * (visibilitychange, pageshow, focus) espacé de 30 s, et la mise à jour
 * passe par rechargerVersionRecente en mode `force` (jamais un
 * rechargement sous l'ancien service worker). `verifierMiseAJour()` sert le
 * bouton « Vérifier les mises à jour » de la page « Plus ».
 * Mikael - chef de projet - 2026-10-07
 */
import { afficherBandeauMiseAJour } from './updateBanner';
import { activerOuSignaler } from './activationOuverture';
import { rechargerVersionRecente } from './rechargerVersionRecente';

// 2026-10-04 (Mika : « l'application doit signaler qu'il faut recharger à
// chaque mise à jour, comme EPS Manager ») : à une heure, un déploiement du
// matin restait invisible toute la matinée. On relit index.html (quelques
// Ko) toutes les 5 minutes tant que l'onglet est visible, et au retour sur
// l'onglet dès qu'une minute s'est écoulée. Mikael - chef de projet
const INTERVALLE_MS = 5 * 60 * 1000;
// 2026-10-07 : 30 s entre deux vérifications au retour (c'était une minute).
const DELAI_MIN_MS = 30 * 1000;
/** 2026-10-07 : vérification au démarrage, une fois la page dessinée. */
export const DELAI_DEMARRAGE_MS = 3000;
const MOTIF_ENTREE = /assets\/index-[A-Za-z0-9_-]+\.js/;

/** L'empreinte de l'entrée qui a chargé la page courante, ou null. */
export function empreinteChargee(doc: Document = document): string | null {
  for (const script of Array.from(doc.scripts)) {
    const m = MOTIF_ENTREE.exec(script.src);
    if (m) return m[0];
  }
  return null;
}

/** L'empreinte de l'entrée référencée par un `index.html`. */
export function empreinteDansHtml(html: string): string | null {
  const m = MOTIF_ENTREE.exec(html);
  return m ? m[0] : null;
}

/**
 * Adresse d'index.html qui CONTOURNE le service worker.
 *
 * Défaut trouvé par le banc à trois versions (2026-10-07) : sous un service
 * worker Workbox, `fetch('index.html')` est servi par la route de PRÉCACHE,
 * quelle que soit l'option `cache` : la page relisait donc SA PROPRE version
 * et concluait « à jour », sur iPhone comme ailleurs, tant que l'ancien
 * service worker était aux commandes. Un paramètre inconnu du précache
 * (`?verif=<instant>`) fait passer la requête au réseau ; le serveur sert
 * index.html quel que soit le paramètre.
 * Mikael - chef de projet - 2026-10-07
 */
export function adresseIndexEnLigne(): string {
  const u = new URL('index.html', document.baseURI);
  u.searchParams.set('verif', String(Date.now()));
  return u.href;
}

/**
 * Empreinte de l'entrée référencée par l'index.html EN LIGNE (relu sans
 * cache), ou null (réseau absent, réponse illisible).
 * Mikael - chef de projet - 2026-10-07
 */
export async function empreinteEnLigne(): Promise<string | null> {
  try {
    const res = await fetch(adresseIndexEnLigne(), { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    return empreinteDansHtml(await res.text());
  } catch {
    return null;
  }
}

/** « assets/index-CaxPbZ96.js » → « CaxPbZ96 » (affichage). */
export function empreinteCourte(empreinte: string | null): string | null {
  if (empreinte === null) return null;
  const m = /index-([A-Za-z0-9_-]+)\.js$/.exec(empreinte);
  return m ? (m[1] ?? null) : null;
}

/**
 * Relit index.html et compare. Retourne true si une nouvelle version est en
 * ligne. Silencieux en cas d'erreur réseau : on réessaiera.
 */
export async function nouvelleVersionEnLigne(): Promise<boolean> {
  const locale = empreinteChargee();
  if (locale === null) return false;
  const distante = await empreinteEnLigne();
  return distante !== null && distante !== locale;
}

/** Résultat du bouton « Vérifier les mises à jour ». */
export type ResultatVerification = 'a_jour' | 'mise_a_jour' | 'injoignable';

/**
 * Bouton « Vérifier les mises à jour » (page « Plus ») : relit index.html
 * sans cache ; si une autre version est en ligne, l'applique (SKIP_WAITING
 * au service worker en attente, puis réinstallation sans l'ancien service
 * worker si la page n'a pas changé de version en quelques secondes).
 * Mikael - chef de projet - 2026-10-07
 */
export async function verifierMiseAJour(
  appliquer: () => void | Promise<void> = () => rechargerVersionRecente(undefined, { force: true }),
): Promise<ResultatVerification> {
  const distante = await empreinteEnLigne();
  if (distante === null) return 'injoignable';
  const locale = empreinteChargee();
  if (locale !== null && distante === locale) return 'a_jour';
  void appliquer();
  return 'mise_a_jour';
}

/**
 * Réaction par défaut à une nouvelle version détectée sans service worker :
 * aux premiers instants (ouverture ou retour au premier plan, sans geste ni
 * saisie, file hors ligne vide), un seul rechargement ; sinon le bandeau.
 * Mikael - chef de projet - 2026-10-04
 */
export function reagirNouvelleVersion(): void {
  // 2026-10-04 : jamais un simple location.reload() : sous l'ancien service
  // worker, il ressert l'ancienne version (voir rechargerVersionRecente.ts).
  // Mikael - chef de projet - 2026-10-04
  const recharger = (): void => {
    // 2026-10-07 : `force` : une autre version est CONSTATÉE en ligne.
    void rechargerVersionRecente(undefined, { force: true });
  };
  void activerOuSignaler({
    activer: recharger,
    signaler: () => afficherBandeauMiseAJour(recharger),
  });
}

/** Démarre la surveillance. Retourne une fonction d'arrêt (tests). */
export function surveillerLaVersion(surNouvelleVersion: () => void = reagirNouvelleVersion): () => void {
  // 2026-10-07 : 0, la première vérification n'attend pas une minute.
  let derniereVerification = 0;
  let signale = false;
  const verifier = async (): Promise<void> => {
    if (signale) return;
    derniereVerification = Date.now();
    if (await nouvelleVersionEnLigne()) {
      signale = true;
      surNouvelleVersion();
    }
  };
  const surVisibilite = (): void => {
    if (document.visibilityState === 'visible' && Date.now() - derniereVerification >= DELAI_MIN_MS) {
      void verifier();
    }
  };
  document.addEventListener('visibilitychange', surVisibilite);
  // Comme EPS Manager (pwa/versionApplication.js) : aussi au retour du focus
  // sur la fenêtre (application installée, bascule entre fenêtres).
  window.addEventListener('focus', surVisibilite);
  // 2026-10-07 : iOS restaure souvent l'application installée depuis son
  // cache de pages (bfcache) : seul `pageshow` le dit.
  window.addEventListener('pageshow', surVisibilite);
  const minuteur = window.setInterval(() => {
    if (document.visibilityState === 'visible') void verifier();
  }, INTERVALLE_MS);
  // 2026-10-07 : au démarrage (ouverture à froid de l'application installée).
  const demarrage = window.setTimeout(surVisibilite, DELAI_DEMARRAGE_MS);
  return () => {
    document.removeEventListener('visibilitychange', surVisibilite);
    window.removeEventListener('focus', surVisibilite);
    window.removeEventListener('pageshow', surVisibilite);
    window.clearInterval(minuteur);
    window.clearTimeout(demarrage);
  };
}
